import React, { useEffect, useState, useCallback } from "react";

//import components
import DropdownSelect from "../../../assets/reusableComponents/dropDownSelect";
import { ToastContainer, toast, Zoom, Bounce } from "react-toastify";
import customToasts from "../../../assets/reusableComponents/myToasts";

//import scss
import "./RentInvoices.scss";

//import icons
import { IoMdAdd } from "react-icons/io";
import { MdEdit } from "react-icons/md";
import { LiaSaveSolid } from "react-icons/lia";
import { TiCancel } from "react-icons/ti";
import { TbDeviceMobileMessage } from "react-icons/tb";


//redux imports
import { useSelector, useDispatch } from "react-redux";
import {
  listRentInvoices,
  createRentInvoice,
  sendRentReminderSms,
} from "../../../../redux/slices/sliceActions";

//import resize function
import { vw, vh } from "../../../assets/reusableComponents/resizeFunction";
import ConfirmationModal from "./ConfirmationModal";
import OriginalInvoiceModal from "./OriginalInvoiceModal";
import ViewOriginalButton from "./ViewOriginalButton";
import MissingWaterInvoices from "./MissingWaterInvoices";
import { hasInvoiceEdits } from "../../../assets/reusableComponents/invoiceEdits";
import {
  RENT_CHARGE_FIELDS,
  getInvoiceChargeValue,
  updateInvoiceEdit,
  validateInvoiceEdits,
} from "./invoiceDraft";

const calculateTotal = (
  rent = 0,
  water = 0,
  garbage = 0,
  service_charge = 0,
  late_fee = 0,
  arrears = 0,
  unpaid_deposit = 0
) => {
  return (
    (Number(rent) || 0) +
    (Number(water) || 0) +
    (Number(garbage) || 0) +
    (Number(service_charge) || 0) +
    (Number(late_fee) || 0) +
    (Number(arrears) || 0) +
    (Number(unpaid_deposit) || 0)
  ).toFixed(0);
};

const sortByHouseNumber = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const houseNumberA =
      a.tenant?.house?.house_no || a.house?.house_no || "";
    const houseNumberB =
      b.tenant?.house?.house_no || b.house?.house_no || "";

    const numA = parseInt(houseNumberA.replace(/^\D+/g, ""), 10) || 0;
    const numB = parseInt(houseNumberB.replace(/^\D+/g, ""), 10) || 0;

    const lettersA = houseNumberA.replace(/\d/g, "").toLowerCase();
    const lettersB = houseNumberB.replace(/\d/g, "").toLowerCase();

    if (lettersA === "" && lettersB === "") {
      return numA - numB;
    }
    if (lettersA === "") return -1;
    if (lettersB === "") return 1;
    if (numA !== numB) return numA - numB;
    return lettersA.localeCompare(lettersB);
  });
};


const RentInvoices = ({ initialFilters, onNavigateToPage }) => {
  // redux methods
  const dispatch = useDispatch();
  const { tenants, properties, rentInvoices } = useSelector(
    (state) => state.landlordslice
  );

  // apartment  variables
  const [apartmentChoices, setApartmentChoices] = useState([]);
  const [selectedApartment, setSelectedApartment] =
    useState(initialFilters?.apartment ?? "Select Apartment");
  useEffect(() => {
    if (Object.keys(properties).length > 0) {
      setApartmentChoices([...properties.map((property) => property.name)]);
    }
  }, [properties]);

  const selectedApartmentObject = properties.find(
    (property) => property.name === selectedApartment
  );

  //month variables
  const [selectedMonth, setSelectedMonth] = useState(initialFilters?.month ?? "Select Month");
  const monthChoices = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  //rent reminder button
  const [showSendRentReminderButton, setShowSendRentReminderButton] =
    useState(false);

  //year variables
  const [selectedYear, setSelectedYear] = useState(initialFilters?.year ?? "Select Year");
  const currentYear = new Date().getFullYear();
  const yearChoices = Array.from({ length: 21 }, (_, i) => currentYear - i);
  const [noSelectionsMade, setNoSelectionsMade] = useState(true);
  const [isInvoicesFetched, setIsInvoicesFetched] = useState(false);

  //invoice variables
  const [showTable, setShowTable] = useState(false);
  const [readingsFetched, setReadingsFetched] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesToCreate, setInvoicesToCreate] = useState([]);
  // NEW: state for ignored invoices (tenants who joined in the selected month and already have invoices)
  const [ignoredInvoices, setIgnoredInvoices] = useState([]);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const hasEditedViewInvoices = [...(invoices[0] || []), ...ignoredInvoices].some(hasInvoiceEdits);

  // ========== DRAFT EDITING STATE ==========
  // Tracks local edits per invoice: { [invoice_id]: { rent: 15000, water: 500, ... } }
  const [editedInvoices, setEditedInvoices] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New toggles for Edit Mode (matching WaterInvoices)
  const [isEditingAll, setIsEditingAll] = useState(false);
  const [editingRows, setEditingRows] = useState({});

  const handleEditAll = () => {
    if (isSubmitting) return;
    if (!isEditingAll) {
      const allTrue = {};
      invoicesToCreate.forEach(inv => allTrue[inv._id || inv.id] = true);
      setEditingRows(allTrue);
      setIsEditingAll(true);
    } else {
      handleSaveDraft();
    }
  };

  const handleRowEditClick = (id) => {
    if (isSubmitting) return;
    setEditingRows(prev => ({ ...prev, [id]: !prev[id] }));
  };


  const editableFields = RENT_CHARGE_FIELDS;

  // Handle individual field edit
  const handleFieldEdit = useCallback((invoice, field, value) => {
    setEditedInvoices((prev) => updateInvoiceEdit(prev, invoice, field, value));
  }, []);

  // Get the display value for a field (edited value or original)
  const getFieldValue = useCallback(
    (invoice, field) => {
      const invoiceId = invoice._id || invoice.id;
      if (editedInvoices[invoiceId]?.[field] !== undefined) {
        return editedInvoices[invoiceId][field];
      }
      return getInvoiceChargeValue(invoice, field);
    },
    [editedInvoices]
  );

  // Calculate total for an invoice with edited values
  const getEditedTotal = useCallback(
    (invoice) => {
      return calculateTotal(
        getFieldValue(invoice, "rent"),
        getFieldValue(invoice, "water"),
        getFieldValue(invoice, "garbage"),
        getFieldValue(invoice, "service_charge"),
        getFieldValue(invoice, "late_fee"),
        getFieldValue(invoice, "arrears"),
        getFieldValue(invoice, "unpaid_deposit")
      );
    },
    [getFieldValue]
  );

  // Save locally for review. Only confirmation writes the edits to the backend.
  const handleSaveDraft = useCallback(() => {
    const error = validateInvoiceEdits(editedInvoices);
    if (error) {
      customToasts.error(error);
      return;
    }
    setEditingRows({});
    setIsEditingAll(false);
    customToasts.success("Changes saved for review. Confirm to submit invoices.");
  }, [editedInvoices]);


  // Reset edit state when selections change
  const resetEditState = useCallback(() => {
    setEditedInvoices({});
    setEditingRows({});
    setIsEditingAll(false);
  }, []);

  //function to check selection of the three
  useEffect(() => {
    setShowTable(false);
    setOriginalInvoice(null);
    resetEditState();
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
      return;
    }
    setIsInvoicesFetched(false); // Reset fetched state when selections change
    const selectedApartmentID = properties.find(
      (property) => property.name == selectedApartment
    )?._id;

    if (selectedApartmentID) {
      const monthNumber = monthChoices.indexOf(selectedMonth) + 1;

      dispatch(
        listRentInvoices({
          property_id: selectedApartmentID,
          month: monthNumber,
          year: selectedYear,
        })
      )
        .unwrap()
        .then((data) => {
          setIsInvoicesFetched(true);
        })
        .catch(() => {
          setIsInvoicesFetched(true); // Still true so button doesn't spin forever
          customToasts.error("Failed to load rent invoices");
        });
    }
    setNoSelectionsMade(false);
  }, [selectedApartment, selectedMonth, selectedYear]);


  useEffect(() => {
    console.log("invoices to create", invoicesToCreate);
  }, [invoicesToCreate]);

  useEffect(() => {
    console.log("rent invoices", rentInvoices);
    // Reset states
    setIgnoredInvoices([]);
    setInvoicesToCreate([]);
    setInvoices([]);

    // Access invoices from rentInvoices.data.invoices (based on the restructured response)
    const invoicesData = rentInvoices?.data?.invoices || rentInvoices?.invoices;
    const ignoredData = rentInvoices?.data?.ignoredInvoices || rentInvoices?.ignoredInvoices || [];
    const hasMeterReadings = rentInvoices?.meterReadings || rentInvoices?.data?.meterReadings;
    const isPropertyCreatedInSameMonth = rentInvoices?.isPropertyCreatedInSameMonth || rentInvoices?.data?.isPropertyCreatedInSameMonth;

    if (hasMeterReadings && ignoredData.length > 0 && isPropertyCreatedInSameMonth) {
      // Property was created in same month — only first invoices exist, no regular invoices yet
      // Show the ignored invoices highlighted in yellow in the view
      setInvoices([]);
      setIgnoredInvoices(sortByHouseNumber(ignoredData));
    } else if (hasMeterReadings) {
      // Existing property — water invoices not created yet, or no ignored invoices
      setInvoices([]);
      setIgnoredInvoices([]);
    } else if (invoicesData?.[0]?.isCreated) {
      setShowSendRentReminderButton(true);
      setInvoices([sortByHouseNumber(invoicesData)]);
      // Also set ignored invoices for display when already created
      if (ignoredData.length > 0) {
        setIgnoredInvoices(sortByHouseNumber(ignoredData));
      }
    } else if (invoicesData && !invoicesData?.[0]?.isCreated) {
      console.log("test", invoicesData);
      setShowSendRentReminderButton(false);
      setInvoicesToCreate(sortByHouseNumber(invoicesData));
      // In view mode before "Create New", keep ignoredInvoices empty so it doesn't prematurely trigger view mode
      setIgnoredInvoices([]);
    }
  }, [isInvoicesFetched, rentInvoices]);

  useEffect(() => {
    console.log("invoices updated", invoices);
  }, [invoices]);

  // NEW: State for loading when clicking Create New
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  //handle create new invoice
  const handleCreateNewInvoice = () => {
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
      customToasts.error("Please fill in all fields");
      return;
    }

    const invoicesData = rentInvoices?.data?.invoices || rentInvoices?.invoices;
    if (invoicesData && invoicesData.length > 0 && invoicesData[0]?.isCreated) {
       customToasts.error("Invoices for this month have already been created");
       return;
    }

    const hasMeterReadings =
      rentInvoices?.meterReadings || rentInvoices?.data?.meterReadings;
    const isPropertyCreatedInSameMonth =
      rentInvoices?.isPropertyCreatedInSameMonth ||
      rentInvoices?.data?.isPropertyCreatedInSameMonth;

    if (hasMeterReadings && !isPropertyCreatedInSameMonth) {
      customToasts.error("Please create water invoices first");
      return;
    }

    if (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) {
      customToasts.error("You can't create an invoice if there are no houses in the property");
      return;
    }

    setIsCreatingNew(true);
    setTimeout(() => {
      setShowTable(true);

      // Populate ignored invoices from raw response for "Create New" display.
      // This ensures first invoices for mid-month new tenants appear highlighted
      // even when the view mode shows "No invoices found" for existing properties.
      const rawIgnored = rentInvoices?.data?.ignoredInvoices || rentInvoices?.ignoredInvoices || [];
      if (rawIgnored.length > 0) {
        setIgnoredInvoices(sortByHouseNumber(rawIgnored));
      } else {
        setIgnoredInvoices([]);
      }

      if (invoicesData && invoicesData.length > 0) {
        setInvoicesToCreate(sortByHouseNumber(invoicesData));
      } else {
        setInvoicesToCreate([]);
      }
      setIsCreatingNew(false);
    }, 800);
  };

  const createInvoiceFunction = () => {
    if (isSubmitting) return;
    const error = validateInvoiceEdits(editedInvoices);
    if (error) {
      customToasts.error(error);
      return;
    }
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
      return;
    }

    const hasMeterReadings =
      rentInvoices?.meterReadings || rentInvoices?.data?.meterReadings;
    const isPropertyCreatedInSameMonth =
      rentInvoices?.isPropertyCreatedInSameMonth ||
      rentInvoices?.data?.isPropertyCreatedInSameMonth;

    if (hasMeterReadings && !isPropertyCreatedInSameMonth) {
      customToasts.error("Please create water invoices first");
      return;
    }

    const selectedApartmentID = properties.find(
      (property) => property.name == selectedApartment
    )?._id;
    if (selectedApartmentID) {
      const monthNumber = monthChoices.indexOf(selectedMonth) + 1;
      setIsSubmitting(true);
      customToasts.promise(
        dispatch(
          createRentInvoice({
            propertyId: selectedApartmentID,
            month: monthNumber,
            year: selectedYear,
            invoices: Object.entries(editedInvoices).map(([invoice_id, changes]) => ({
              invoice_id,
              ...changes,
            })),
          })
        ).then((res) => {
          if (res?.payload?.status === "success") {
            resetEditState();
            setShowTable(false);
            dispatch(
              listRentInvoices({
                property_id: selectedApartmentID,
                month: monthNumber,
                year: selectedYear,
              })
            );
          } else {
            throw new Error(res?.payload?.message || "Failed to create invoices");
          }
        }),
        {
          pending: "Creating Invoices...",
          success: "Invoices created successfully!",
          error: "Failed to create invoice",
        }
      ).catch(() => {}).finally(() => setIsSubmitting(false));
    }
  };

  const handleCancelInvoice = () => {
    if (isSubmitting) return;
    resetEditState();
    // Reset the reading data back to original state
    setSelectedApartment("Select Apartment");
    setSelectedMonth("Select Month");
    setSelectedYear("Select Year");
    setNoSelectionsMade(true);
    setShowTable(false);
    setReadingsFetched([]);
    setIgnoredInvoices([]);
    setInvoicesToCreate([]);
    customToasts.info("Invoice creation cancelled");
  };

  const [animateButton, setAnimateButton] = useState(false);

  const handleSendRentReminder = () => { 
    if (
      selectedApartment === "Select Apartment" ||
      selectedMonth === "Select Month" ||
      selectedYear === "Select Year"
    ) {
     
      return;
    }
    const selectedApartmentID = properties.find(
      (property) => property.name == selectedApartment
    )?._id;

    if (selectedApartmentID) {
      const monthNumber = monthChoices.indexOf(selectedMonth) + 1;

      customToasts.promise(
        dispatch(
          sendRentReminderSms({
            property_id: selectedApartmentID,
            month: monthNumber,
            year: selectedYear,
          }),
        ).unwrap(),
        {
          pending: "Sending Rent Reminder...",
          success: "Rent Reminder sent successfully!",
          error: "Failed to send Rent Reminder",
        }
      );
    }
  }

  useEffect(() => {
    // Set animateButton to true when there are no invoices (regular or ignored) and selections have been made and water invoices are ready
    const hasMeterReadings =
      rentInvoices?.meterReadings || rentInvoices?.data?.meterReadings;
    const isPropertyCreatedInSameMonth =
      rentInvoices?.isPropertyCreatedInSameMonth ||
      rentInvoices?.data?.isPropertyCreatedInSameMonth;
    const waterInvoicesNotCreated =
      hasMeterReadings && !isPropertyCreatedInSameMonth;

    setAnimateButton(
      isInvoicesFetched &&
      !noSelectionsMade &&
        invoices.length === 0 &&
        ignoredInvoices.length === 0 &&
        !showTable &&
        !waterInvoicesNotCreated &&
        (selectedApartmentObject?.houses && selectedApartmentObject.houses.length > 0)
    );
  }, [isInvoicesFetched, noSelectionsMade, invoices.length, ignoredInvoices.length, showTable, rentInvoices, selectedApartmentObject]);

  //modal state
  const [showModal, setShowModal] = useState(false);

  const invoicesData = rentInvoices?.data?.invoices || rentInvoices?.invoices;
  
  const hasMeterReadingsForStyle =
    rentInvoices?.meterReadings || rentInvoices?.data?.meterReadings;
  const isPropertyCreatedInSameMonthForStyle =
    rentInvoices?.isPropertyCreatedInSameMonth ||
    rentInvoices?.data?.isPropertyCreatedInSameMonth;
  const waterInvoicesNotCreated =
    hasMeterReadingsForStyle && !isPropertyCreatedInSameMonthForStyle;

  return (
    <div className="rent-invoices-page">
      <div className="title-section">
        <div className="title-and-description">
          <div className="title">Rent Invoices</div>
          <div className="description">View Rent Invoice History</div>
        </div>
        <div
          className={`create-expense-button ${
            animateButton ? "animate-button" : ""
          }`}
          onClick={isCreatingNew || !isInvoicesFetched ? undefined : handleCreateNewInvoice}
          style={{
            opacity: (invoicesData?.length > 0 && invoicesData[0]?.isCreated) || isCreatingNew || !isInvoicesFetched || waterInvoicesNotCreated || (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? 0.5 : 1,
            cursor: (invoicesData?.length > 0 && invoicesData[0]?.isCreated) || isCreatingNew || !isInvoicesFetched || waterInvoicesNotCreated || (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? 'not-allowed' : 'pointer'
          }}
        >
          {isCreatingNew ? (
            <div className="spinner" style={{ width: "20px", height: "20px", border: "2px solid #fff", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
          ) : (
            <IoMdAdd />
          )}
          Create New
        </div>
      </div>

      <div className="long-rectangle">
        <div className="overlay">
          <div className="left-rectangle">
            <div className="title">All Rent Invoices</div>
            <div className="bottom-section">
              <div className="filters-section">
                <div className="box">
                  <div className="box-title">Apartment Name</div>
                  <div className="select">
                    <DropdownSelect
                      choices={apartmentChoices}
                      defaultChoice={selectedApartment}
                      onSelect={setSelectedApartment}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
                <div className="box">
                  <div className="box-title">Month </div>
                  <div className="select">
                    <DropdownSelect
                      choices={monthChoices}
                      defaultChoice={selectedMonth}
                      onSelect={setSelectedMonth}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
                <div className="box">
                  <div className="box-title">Select Year</div>
                  <div className="select">
                    <DropdownSelect
                      choices={yearChoices}
                      defaultChoice={selectedYear}
                      onSelect={setSelectedYear}
                      backgroundColor={"#ffffff"}
                      textColor={"#192a3a"}
                      border={"none"}
                    />
                  </div>
                </div>
              </div>
              <div className="description">
                Apply filters to view specific invoices
              </div>
            </div>
          </div>
        </div>
      </div>

      {noSelectionsMade ? (
        <div className="no-selections-made">
          <div className="text">
            Select the relevant fields to view invoices
          </div>
        </div>
      ) : !isInvoicesFetched ? (
        <div className="no-selections-made">
          <div className="text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <div className="spinner" style={{ width: "40px", height: "40px", border: "4px solid #00B1A9", borderTop: "4px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
            Fetching rent invoices...
          </div>
        </div>
      ) : (
        <>
          {!showTable && (invoices.length > 0 || ignoredInvoices.length > 0) ? (
            <>
              <div className="invoice-grid" style={{ "--invoice-columns": hasEditedViewInvoices ? 10 : 9 }}>
                {/* NEW: Informational banner for ignored invoices when invoices are already created */}
                {ignoredInvoices.length > 0 && (
                  <div
                    className="ignored-banner"
                    style={{
                      width: "97%",
                      margin: `${vh(10)} 0px`,
                      padding: `${vh(12)} ${vw(20)}`,
                      borderRadius: `${vw(6)}`,
                      background: "#FFF7E6",
                      color: "#5a3d00",
                    }}
                  >
                    The highlighted invoices represent tenants who joined in
                    the selected month. These invoices were generated when the tenant was added.
                  </div>
                )}
                <div
                  className="title-box"
                  style={{ margin: `${vh(20)} 0px 0px 0px` }}
                >
                  <div className="title">House Number</div>
                  <div className="title">Rent</div>
                  <div className="title">Water Invoice</div>
                  <div className="title">Garbage</div>
                  <div className="title">Service Fee</div>
                  <div className="title">Late Fee</div>
                  <div className="title">Arrears</div>
                  <div className="title">Unpaid Deposit</div>
                  <div className="title">Total</div>
                  {hasEditedViewInvoices && <div className="title">View original</div>}
                </div>
                <div className="invoice-box">
                  {invoices?.[0]?.map((invoice) => (
                    <div key={invoice._id || invoice.id} className={`invoice-row ${hasInvoiceEdits(invoice) ? "edited-row" : ""}`} title={hasInvoiceEdits(invoice) ? "Manually changed" : undefined}>
                      <div className="invoice-data">
                        {invoice?.tenant?.house?.house_no}
                      </div>
                      <div className="invoice-data">{getInvoiceChargeValue(invoice, "rent")}</div>
                      <div className="invoice-data">{invoice?.water ?? 0}</div>
                      <div className="invoice-data">{invoice?.garbage ?? 0}</div>
                      <div className="invoice-data">{invoice?.service_charge ?? 0}</div>
                      <div className="invoice-data">{invoice?.late_fee ?? 0}</div>
                      <div className="invoice-data">{invoice?.arrears ?? 0}</div>
                      <div className="invoice-data">{invoice?.unpaid_deposit ?? 0}</div>

                      <div className="invoice-data">
                        {calculateTotal(
                          getInvoiceChargeValue(invoice, "rent"),
                          invoice?.water,
                          invoice?.garbage,
                          invoice?.service_charge,
                          invoice?.late_fee,
                          invoice?.arrears,
                          invoice?.unpaid_deposit
                        )}
                      </div>
                      {hasEditedViewInvoices && (
                        <div className="invoice-data">
                          {hasInvoiceEdits(invoice) && <ViewOriginalButton onClick={() => setOriginalInvoice(invoice)} />}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* NEW: Render ignored invoices with highlighted styling */}
                  {ignoredInvoices.map((invoice) => (
                    <div
                      key={`ignored-${invoice._id}`}
                      className={`invoice-row ignored-row ${hasInvoiceEdits(invoice) ? "edited-row" : ""}`}
                    >
                      <div className="invoice-data">
                        {invoice?.tenant?.house?.house_no}
                      </div>
                      <div className="invoice-data">{getInvoiceChargeValue(invoice, "rent")}</div>
                      <div className="invoice-data">{invoice?.water ?? 0}</div>
                      <div className="invoice-data">{invoice?.garbage ?? 0}</div>
                      <div className="invoice-data">{invoice?.service_charge ?? 0}</div>
                      <div className="invoice-data">{invoice?.late_fee ?? 0}</div>
                      <div className="invoice-data">{invoice?.arrears ?? 0}</div>
                      <div className="invoice-data">{invoice?.unpaid_deposit ?? 0}</div>
                      <div className="invoice-data">
                        {invoice?.total ||
                          calculateTotal(
                            getInvoiceChargeValue(invoice, "rent"),
                            invoice?.water || 0,
                            invoice?.garbage || 0,
                            invoice?.service_charge || 0,
                            invoice?.late_fee || 0,
                            invoice?.arrears || 0,
                            invoice?.unpaid_deposit || 0
                          )}
                      </div>
                      {hasEditedViewInvoices && (
                        <div className="invoice-data">
                          {hasInvoiceEdits(invoice) && <ViewOriginalButton onClick={() => setOriginalInvoice(invoice)} />}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="buttons-container">
                  {/* Add the new button that only shows when showSendRentReminderButton is true */}
                  {showSendRentReminderButton ? (
                    <div
                      className="send-reminder-button"
                      onClick={() => {
                        handleSendRentReminder() 
                        
                      }}
                    >
                      <div className="text">Send Invoices</div>
                     <TbDeviceMobileMessage />
                      {/* You might want to change this icon to something more appropriate */}
                    </div>
                  ) : (
                    <>
                      <div
                        className="confirm-button"
                        onClick={() => setShowModal(true)}
                      >
                        <div className="text">{isSubmitting ? "Submitting..." : "Confirm"}</div>
                        <LiaSaveSolid />
                      </div>
                      <div
                        className="cancel-button"
                        onClick={handleCancelInvoice}
                      >
                        <div className="text">Cancel</div>
                        <TiCancel />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {showTable ? (
                <>
                  <div className="invoice-grid">
                    {/* NEW: Informational banner for ignored invoices */}
                    {ignoredInvoices.length > 0 && (
                      <div
                        className="ignored-banner"
                        style={{
                          width: "97%",
                          margin: `${vh(10)} 0px`,
                          padding: `${vh(12)} ${vw(20)}`,
                          borderRadius: `${vw(6)}`,
                          background: "#FFF7E6",
                          color: "#5a3d00",
                        }}
                      >
                        The highlighted invoices represent tenants who joined in
                        the selected month and already have rent invoices
                        generated. These invoices were created when the tenant was added
                        and will not be modified when you confirm.
                      </div>
                    )}




                    <div
                      className="title-box"
                      style={{ margin: `${vh(20)} 0px 0px 0px` }}
                    >
                      <div className="title">House Number</div>
                      <div className="title">Rent</div>
                      <div className="title">Water Invoice</div>
                      <div className="title">Garbage</div>
                      <div className="title">Service Fee</div>
                      <div className="title">Late Fee</div>
                      <div className="title">Arrears</div>
                      <div className="title">Unpaid Deposit</div>
                      <div className="title">Total</div>
                      <div className="title">
                        {isEditingAll ? (
                          <div
                            onClick={handleEditAll}
                            className="actions-button"
                          >
                            <div className="text">Save All</div>
                            <LiaSaveSolid />
                          </div>
                        ) : (
                          <div
                            onClick={handleEditAll}
                            className="actions-button"
                          >
                            <div className="text">Edit All</div>
                            <MdEdit />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="invoice-box">
                      {/* Render ignored invoices first with highlighted styling (read-only) */}
                      {ignoredInvoices.map((invoice) => (
                        <div
                          key={`ignored-${invoice._id}`}
                          className={`invoice-row ignored-row ${hasInvoiceEdits(invoice) ? "edited-row" : ""}`}
                        >
                          <div className="invoice-data">
                            {invoice?.tenant?.house?.house_no}
                          </div>
                          <div className="invoice-data">{getInvoiceChargeValue(invoice, "rent")}</div>
                          <div className="invoice-data">{invoice?.water ?? 0}</div>
                          <div className="invoice-data">{invoice?.garbage ?? 0}</div>
                          <div className="invoice-data">{invoice?.service_charge ?? 0}</div>
                          <div className="invoice-data">{invoice?.late_fee ?? 0}</div>
                          <div className="invoice-data">{invoice?.arrears ?? 0}</div>
                          <div className="invoice-data">{invoice?.unpaid_deposit ?? 0}</div>
                          <div className="invoice-data">
                            {invoice?.total ||
                              calculateTotal(
                                getInvoiceChargeValue(invoice, "rent"),
                                invoice?.water || 0,
                                invoice?.garbage || 0,
                                invoice?.service_charge || 0,
                                invoice?.late_fee || 0,
                                invoice?.arrears || 0,
                                invoice?.unpaid_deposit || 0
                              )}
                          </div>
                          <div className="invoice-data"></div>
                        </div>
                      ))}

                      {/* EDITABLE regular invoices to create */}
                      {invoicesToCreate?.map((invoice) => {
                        const invoiceId = invoice._id || invoice.id;
                        const isEdited = Boolean(editedInvoices[invoiceId]) || hasInvoiceEdits(invoice);
                        const isRowEditing = editingRows[invoiceId];
                        return (
                          <div
                            key={invoiceId}
                            className={`invoice-row ${isEdited ? "edited-row" : ""} ${isRowEditing ? "is-editing" : ""}`}
                            title={isEdited ? "This invoice has manual changes" : ""}
                          >
                            <div className="invoice-data">
                              {invoice?.tenant?.house?.house_no}
                            </div>
                            {editableFields.map((field) => (
                              <div key={field.key} className="invoice-data">
                                {isRowEditing ? (
                                  <input
                                    type="number"
                                    value={getFieldValue(invoice, field.key)}
                                    onChange={(e) =>
                                      handleFieldEdit(invoice, field.key, e.target.value)
                                    }
                                    className="editable-input"
                                    disabled={isSubmitting}
                                    min={["arrears", "late_fee"].includes(field.key) ? undefined : "0"}
                                    step="1"
                                    placeholder="0"
                                  />
                                ) : (
                                  getFieldValue(invoice, field.key)
                                )}
                              </div>
                            ))}
                            <div className="invoice-data">
                              {getEditedTotal(invoice)}
                            </div>
                            <div
                              className="invoice-data"
                              style={{ color: "#B39359", cursor: "pointer" }}
                              onClick={() => handleRowEditClick(invoiceId)}
                            >
                              <MdEdit />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="buttons-container">
                    <div
                      className={`confirm-button ${isSubmitting ? "disabled" : ""}`}
                      onClick={() => {
                        if (isSubmitting) return;
                        const error = validateInvoiceEdits(editedInvoices);
                        if (error) {
                          customToasts.error(error);
                          return;
                        }
                        setShowModal(true);
                      }}
                      style={{
                        opacity: isSubmitting ? 0.5 : 1,
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      <div className="text">{isSubmitting ? "Submitting..." : "Confirm"}</div>
                      <LiaSaveSolid />
                    </div>
                    <div
                      className="cancel-button"
                      onClick={handleCancelInvoice}
                    >
                      <div className="text">Cancel</div>
                      <TiCancel />
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-selections-made">
                  {waterInvoicesNotCreated ? (
                    <MissingWaterInvoices apartment={selectedApartment} month={selectedMonth} year={selectedYear} onNavigateToPage={onNavigateToPage} />
                  ) : (!selectedApartmentObject?.houses || selectedApartmentObject.houses.length === 0) ? (
                    <div className="text">
                      There are no houses in {selectedApartment} yet. <br />
                      Please add houses to this property first.
                    </div>
                  ) : (
                    <div className="text">
                      No rent invoices have been created for {selectedApartment} in{" "}
                      {selectedMonth} {selectedYear} <br />
                      Click Create New to create rent invoices
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
      {originalInvoice && <OriginalInvoiceModal invoice={originalInvoice} onClose={() => setOriginalInvoice(null)} />}
      {showModal && (
        <ConfirmationModal
          setShowModal={setShowModal}
          title="Create Invoice Confirmation"
          message="Are you sure you want to create this invoice? This action cannot be undone."
          confirmText="Create Invoice"
          cancelText="Cancel"
          onConfirm={createInvoiceFunction}
          onCancel={handleCancelInvoice}
        />
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

export default RentInvoices;
